import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShieldCheck, ArrowRight, HelpCircle, Wallet, Headphones,
} from "lucide-react";
import { PARTNER_STEPS, PARTNER_FAQ, PARTNER_PRIVATE } from "@/data/partner";

/* PartnerResources · lives under PartnerApply on /partner. Two blocks:
   1. How it works · 5-step rail at full section width
   2. FAQ + sidebar · two-column layout that respects the
      section header width (FAQ accordion left, payout + partner-support
      cards on a sticky sidebar right) */
export default function PartnerResources() {
  const { t } = useTranslation();
  return (
    <section data-testid="partner-resources" id="partner-resources" className="relative py-14 lg:py-20 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">

        {/* ===== 1 · How it works · 5 steps ===== */}
        <div className="text-center mb-10" data-testid="partner-how-it-works-header">
          <div className="pill-mono inline-flex mb-3">{t("comp.partner.howItWorksPill", "How partnerships work")}</div>
          <h2 className="font-display text-2xl/[1.25] lg:text-3xl/[1.25] font-medium tracking-[-0.02em] mb-3">
            {t("comp.partner.howItWorksTitle", "Five steps from apply to")}{" "}
            <span className="text-gradient-brand font-semibold">{t("comp.partner.howItWorksAccent", "first payout")}</span>
          </h2>
          <p className="text-text-3 max-w-2xl mx-auto text-[14.5px] lg:text-[15px] font-light leading-relaxed">
            {t("comp.partner.howItWorksBody", "Every partner · IB, content creator, prop educator, brand · runs through the same onboarding. No surprise rules, no take-backs on what you've earned.")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-16 lg:mb-20">
          {PARTNER_STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`partner-step-${i}`}
              className="card-beam p-5 flex flex-col gap-3"
            >
              <span
                className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
                style={{
                  background: "color-mix(in srgb, var(--brand) 8%, transparent)",
                  border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
                }}
              >
                <s.icon className="text-brand" size={17} strokeWidth={2.2} />
              </span>
              <div className="font-display text-[15px] font-medium text-text-1 leading-tight">
                {s.title}
              </div>
              <p className="text-[12.5px] text-text-3 leading-relaxed font-light">{s.body}</p>
            </motion.div>
          ))}
        </div>

        {/* ===== 2 · FAQ + sidebar · two-column full-width layout ===== */}
        <div className="text-center mb-10" data-testid="partner-faq-header">
          <div className="pill-mono inline-flex mb-3">
            <HelpCircle size={11} className="mr-1" /> {t("comp.partner.faqPill")}
          </div>
          <h2 className="font-display text-2xl/[1.25] lg:text-3xl/[1.25] font-medium tracking-[-0.02em] mb-3">
            {t("comp.partner.faqTitle")}{" "}
            <span className="text-gradient-brand font-semibold">{t("comp.partner.faqAccent")}</span>
          </h2>
          <p className="text-text-3 max-w-2xl mx-auto text-[14.5px] lg:text-[15px] font-light leading-relaxed">
            {t("comp.partner.faqBody")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 lg:gap-6 mb-12 lg:mb-16">

          {/* LEFT · FAQ accordion */}
          <div className="card-beam p-4 lg:p-6">
            <Accordion type="single" collapsible className="space-y-1.5" data-testid="partner-faq-accordion">
              {PARTNER_FAQ.map((it, i) => (
                <AccordionItem
                  key={it.q}
                  value={`p-faq-${i}`}
                  className="border-0 rounded-xl px-4 lg:px-5 data-[state=open]:bg-[color:var(--surface-2)] transition-colors"
                  data-testid={`partner-faq-${i}`}
                >
                  <AccordionTrigger className="font-display text-left text-text-1 text-[13.5px] lg:text-[14.5px] font-medium hover:no-underline py-3.5 gap-3">
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-2 text-[13px] leading-relaxed pb-4 font-light">
                    {it.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* RIGHT · sticky sidebar with payout schedule + partner support */}
          <aside className="space-y-5 lg:sticky lg:top-24 self-start">

            {/* Payout schedule card */}
            <div className="card-beam p-5 lg:p-6" data-testid="partner-payout-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="relative w-8 h-8 grid place-items-center">
                  <div aria-hidden className="absolute inset-0 rounded-full"
                       style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 18%, transparent), transparent 70%)", filter: "blur(2px)" }} />
                  <Wallet size={15} strokeWidth={1.8} style={{ color: "var(--brand)", position: "relative", zIndex: 1 }} />
                </div>
                <div>
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">{t("comp.partner.atAGlance")}</div>
                  <h3 className="font-display text-[15px] font-semibold text-text-1 leading-tight">{t("comp.partner.payoutSchedule")}</h3>
                </div>
              </div>
              <ul className="space-y-0" data-testid="partner-payout-list">
                {PARTNER_PRIVATE.payoutSchedule.map((r, i) => (
                  <li
                    key={r.row}
                    className="flex items-center justify-between py-2.5 text-[12.5px]"
                    style={{ borderTop: i === 0 ? "none" : "1px solid color-mix(in srgb, var(--line) 60%, transparent)" }}
                  >
                    <span className="text-text-3 font-light">{r.row}</span>
                    <span className="font-mono font-semibold text-text-1 tabular-nums text-right">{r.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Partner support card · replaces the asset library since we
                don't currently ship a creative kit · this surfaces the
                things we DO actually give partners on day one. */}
            <div className="card-beam p-5 lg:p-6" data-testid="partner-support-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="relative w-8 h-8 grid place-items-center">
                  <div aria-hidden className="absolute inset-0 rounded-full"
                       style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 18%, transparent), transparent 70%)", filter: "blur(2px)" }} />
                  <Headphones size={15} strokeWidth={1.8} style={{ color: "var(--brand)", position: "relative", zIndex: 1 }} />
                </div>
                <div>
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">{t("comp.partner.everyPartnerGets")}</div>
                  <h3 className="font-display text-[15px] font-semibold text-text-1 leading-tight">{t("comp.partner.partnerSupport")}</h3>
                </div>
              </div>
              <ul className="space-y-1.5" data-testid="partner-support-list">
                {PARTNER_PRIVATE.support.map((a, i) => (
                  <li
                    key={a.name}
                    className="flex items-start gap-2.5 py-1.5"
                    data-testid={`partner-support-${i}`}
                  >
                    <a.icon className="text-brand shrink-0 mt-0.5" size={13} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-text-1 leading-tight">{a.name}</div>
                      <div className="text-[9.5px] text-text-3 uppercase tracking-[0.16em] font-mono mt-0.5">{a.note}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <a
                href="mailto:support@ofinancial.markets?subject=Partner%20enquiry"
                className="mt-4 inline-flex items-center gap-1.5 text-brand hover:underline text-[12px] font-medium"
                data-testid="partner-support-contact"
              >
                {t("comp.partner.messageTeam")} <ArrowRight size={12} />
              </a>
            </div>
          </aside>
        </div>

      </div>
    </section>
  );
}
